import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Form, Button, Card, Spinner, Image } from "react-bootstrap";
import Swal from "sweetalert2";
import { _post } from "../../services/apiClient";
import { apiUrls } from "../../config/apiUrls.const";
import { useBusiness } from "../../contexts/BusinessContext";
import { allowOnlyNumbers } from "../../utils/AllowOnlyNumbers";
import TextareaInput from "../components/TextareaInput";
import AlertService from "../../services/AlertService";

const ViewItem: React.FC = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const { business } = useBusiness();
  const [businessItemId] = useState<any>(state?.businessItemId);

  const [itemData, setItemData] = useState<any>(null);
  const [hours, setHours] = useState<number>(0);
  const [minutes, setMinutes] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [existingImages, setExistingImages] = useState<string[]>([]);

  // ✅ Move fetchItemData outside of useEffect
  const fetchItemData = async () => {
    if (!businessItemId) return;
    setLoading(true);
    try {
      const res: any = await _post(apiUrls.getItem, { business_item_id: businessItemId });

      if (res.data?.success) {
        const data = res.data.data;
        setItemData(data);

        // Parse duration
        if (data.duration && typeof data.duration === "string") {
          const [h, m] = data.duration.split(":").map(Number);
          setHours(h || 0);
          setMinutes(m || 0);
        }

        // Images
        if (Array.isArray(data.images)) {
          setExistingImages(data.images);
        }
      } else {
        Swal.fire("Error", res.data?.message || "Failed to fetch item data.", "error");
      }
    } catch (err) {
      Swal.fire("Error", "An error occurred while fetching item data.", "error");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Fetch item data
  useEffect(() => {
    fetchItemData();
  }, [businessItemId]);

  const handleRemoveImage = async (index: number, isExisting: boolean) => {
    if (isExisting) {
      const deleteImage:any = existingImages[index];
      
      if(deleteImage.is_default){
        AlertService.warning('Default image can not be deleted!');
        return;
      }

      const deleteExistingImage = await _post(apiUrls.deleteItemImage, {business_item_image_id: deleteImage?.id});

      if(deleteExistingImage?.data?.success){
        setExistingImages((prev) => prev.filter((_, i) => i !== index));
        await Swal.fire({
          icon: "success",
          title: "Updated!",
          text: "Image has been deleted successfully.",
          timer: 1800,
          showConfirmButton: false,
        });
      }
    }
  };

  if (!itemData) {
    return (
      <div className="text-center mt-5">
        {loading ? <Spinner animation="border" /> : <p>No item found.</p>}
        <Button variant="secondary" onClick={() => navigate(-1)}>
          Go Back
        </Button>
      </div>
    );
  }

    async function handleSetDefaultImage(idx: number): Promise<void> {
        const setDefaultImage:any = existingImages[idx];

        const updateDefaultImage = await _post(apiUrls.setDefaultItemImage, {
            business_item_id: setDefaultImage?.business_item_id,
            business_item_image_id: setDefaultImage?.id
        });

        if(updateDefaultImage?.data?.success){
            await fetchItemData();
            await Swal.fire({
            icon: "success",
            title: "Updated!",
            text: "Image has been set default successfully.",
            timer: 1800,
            showConfirmButton: false,
            });
        }
    }

  // ✅ Main render
  return (
    <div className="container-fluid p-0">
      <h1 className="h3 mb-3">
        {itemData.name_en || itemData.item?.name_en}:{" "}
        <span className="text-secondary">view info</span>
        <Button className="float-end" variant="dark" size="sm" onClick={() => navigate(-1)}>
          Back
        </Button>
      </h1>

      <Card className="shadow-sm">
        <Card.Body>
            <div className="row">
                <div className="col-sm-6">
                    <div className="d-block mb-3">
                        Duration: <b>{hours} hrs {minutes} mins</b>
                    </div>
                    <div className="d-block mb-3">
                        Price: <b>{itemData?.price} BDT</b>
                    </div>

                    <div className="d-block mb-3">
                        Description: <p>{itemData?.description}</p>
                    </div>
                </div>
                <div className="col-sm-6">
                    <div className="d-block">
                        <b>Images</b>
                    </div>
                    <div className="row mt-3">
                    {existingImages.map((img:any, idx) => (
                        <div key={`exist-${idx}`} className="col-4 mb-3 position-relative">
                        <Image
                            src={img?.image_url}
                            thumbnail
                            style={{ width: "100%", height: "150px", objectFit: "cover" }}
                        />
                        {!img?.is_default && (
                            <Button
                                variant="success"
                                size="sm"
                                className="w-100"
                                onClick={() => handleSetDefaultImage(idx)}
                                >
                                Set as Default
                            </Button>
                        )}
                        {!img?.is_default && (
                            <Button
                            variant="danger"
                            size="sm"
                            className="position-absolute top-0 end-0"
                            onClick={() => handleRemoveImage(idx, true)}
                            >
                            X
                            </Button>
                        )}
                        </div>
                    ))}
                    </div>
                </div>
                <div className="col-12">
                    <div className="text-end mt-4">
                        <Button
                        variant="secondary"
                        onClick={() => navigate(-1)}
                        disabled={loading}
                        >
                        Back to previous
                        </Button>
                    </div>
                </div>
            </div>
        </Card.Body>
      </Card>
    </div>
  );
};

export default ViewItem;
